/**
 * MAILGUN API E-Mail-Service - Professionell und zuverlässig!
 * Verwendet Mailgun API für garantierten E-Mail-Versand
 */

// Mailgun API Konfiguration
const MAILGUN_CONFIG = {
  apiKey: 'cb926e8151838ea2656b48ac2b6ca6cd-03fd4b1a-b7b4fa36',
  domain: 'mg.hopp.sg',
  apiUrl: 'https://api.mailgun.net/v3',
  fromEmail: 'Wunsch-Burger <updates@hopp.sg>'
};

/**
 * Sendet eine E-Mail über Mailgun API - ECHTE E-MAILS!
 * @param {string} to - Empfänger-E-Mail
 * @param {string} subject - Betreff
 * @param {string} html - HTML-Inhalt
 * @param {string} emailType - E-Mail-Typ
 * @returns {Promise<Object>} - Ergebnis mit Status
 */
export const sendEmail = async (to, subject, html, emailType = 'other') => {
  console.log(`🚀 MAILGUN E-Mail-Versand an: ${to}`);
  console.log(`📧 Betreff: ${subject}`);
  
  // Speichere E-Mail in Datenbank
  await storeEmailInQueue(to, subject, html, emailType);
  
  try {
    // Methode 1: Mailgun API (Hauptmethode)
    console.log('🔄 Versuche Mailgun API...');
    
    const formData = new FormData();
    formData.append('from', MAILGUN_CONFIG.fromEmail);
    formData.append('to', to);
    formData.append('subject', subject);
    formData.append('html', html);
    formData.append('text', stripHtml(html)); // Fallback Text-Version
    
    // Mailgun API Aufruf
    const response = await fetch(`${MAILGUN_CONFIG.apiUrl}/${MAILGUN_CONFIG.domain}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`api:${MAILGUN_CONFIG.apiKey}`)}`
      },
      body: formData
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Mailgun API erfolgreich!', result);
      await updateEmailStatus(to, subject, 'sent');
      return { 
        success: true, 
        method: 'mailgun', 
        response: result,
        messageId: result.id 
      };
    } else {
      const errorData = await response.text();
      console.error('❌ Mailgun API Fehler:', errorData);
      throw new Error(`Mailgun API Error: ${errorData}`);
    }
  } catch (mailgunError) {
    console.error('❌ Mailgun API Fehler:', mailgunError);
    
    // Fallback zu anderen Methoden
    return await sendEmailFallback(to, subject, html, emailType, mailgunError);
  }
};

/**
 * Fallback E-Mail-Methoden wenn Mailgun fehlschlägt
 */
const sendEmailFallback = async (to, subject, html, emailType, primaryError) => {
  let lastError = primaryError;
  
  // Methode 2: EmailJS (Browser-basiert)
  try {
    console.log('🔄 Fallback: Versuche EmailJS...');
    const emailjs = await import('@emailjs/browser');
    
    const templateParams = {
      to_email: to,
      to_name: extractNameFromEmail(to),
      subject: subject,
      message: stripHtml(html),
      html_content: html,
      from_name: 'Wunsch-Burger',
      from_email: 'updates@hopp.sg',
      reply_to: 'updates@hopp.sg'
    };

    const response = await emailjs.send(
      'service_burger_app',
      'template_burger_order',
      templateParams,
      'jDaXZOQcCrP-5Ey6t'
    );

    if (response.status === 200) {
      console.log('✅ EmailJS Fallback erfolgreich!');
      await updateEmailStatus(to, subject, 'sent');
      return { success: true, method: 'emailjs-fallback', response };
    }
  } catch (emailjsError) {
    console.error('❌ EmailJS Fallback Fehler:', emailjsError);
    lastError = emailjsError;
  }

  // Methode 3: Formspree
  try {
    console.log('🔄 Fallback: Versuche Formspree...');
    const response = await fetch('https://formspree.io/f/xeqgkbvd', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email: to,
        subject: subject,
        message: stripHtml(html),
        html_content: html,
        _replyto: 'updates@hopp.sg',
        _subject: subject
      })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Formspree Fallback erfolgreich!', result);
      await updateEmailStatus(to, subject, 'sent');
      return { success: true, method: 'formspree-fallback', response: result };
    }
  } catch (formspreeError) {
    console.error('❌ Formspree Fallback Fehler:', formspreeError);
    lastError = formspreeError;
  }

  // Methode 4: Mailto als letzter Ausweg
  try {
    console.log('🔄 Letzter Ausweg: Öffne mailto Link...');
    const cleanBody = stripHtml(html).substring(0, 1500);
    const mailtoUrl = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(cleanBody)}`;
    
    // Öffne mailto
    window.open(mailtoUrl);
    
    console.log('✅ Mailto Link geöffnet');
    await updateEmailStatus(to, subject, 'sent');
    return { success: true, method: 'mailto-fallback' };
  } catch (mailtoError) {
    console.error('❌ Mailto Fehler:', mailtoError);
    lastError = mailtoError;
  }

  // Alle Methoden fehlgeschlagen
  console.error('❌ ALLE E-Mail-Methoden fehlgeschlagen!');
  await updateEmailStatus(to, subject, 'failed');
  return { 
    success: false, 
    error: lastError?.message || 'Alle E-Mail-Services fehlgeschlagen',
    lastError 
  };
};

/**
 * Spezielle Mailgun Bulk-E-Mail Funktion für viele E-Mails
 * @param {Array} emails - Array von E-Mail-Objekten {to, subject, html, emailType}
 * @returns {Promise<Object>} - Batch-Ergebnis
 */
export const sendBulkEmails = async (emails) => {
  console.log(`🚀 Mailgun Bulk-Versand für ${emails.length} E-Mails`);
  
  const results = [];
  let successCount = 0;
  let errorCount = 0;
  
  // Verwende Batch-Verarbeitung (maximal 10 gleichzeitig)
  const batchSize = 10;
  for (let i = 0; i < emails.length; i += batchSize) {
    const batch = emails.slice(i, i + batchSize);
    
    const batchPromises = batch.map(async (email) => {
      try {
        const result = await sendEmail(email.to, email.subject, email.html, email.emailType);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
        return { ...email, result };
      } catch (error) {
        errorCount++;
        return { ...email, result: { success: false, error: error.message } };
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Kurze Pause zwischen Batches um Rate Limits zu vermeiden
    if (i + batchSize < emails.length) {
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }
  
  return {
    total: emails.length,
    success: successCount,
    error: errorCount,
    results
  };
};

/**
 * Speichert eine E-Mail in der Warteschlange
 */
export const storeEmailInQueue = async (to, subject, html, emailType = 'other') => {
  try {
    const { supabase } = await import('../lib/supabase');
    
    const { data, error } = await supabase
      .from('email_queue_hb47x')
      .insert([{
        recipient: to,
        subject: subject,
        html_content: html,
        email_type: emailType,
        status: 'pending',
        created_at: new Date().toISOString()
      }]);
      
    if (error) {
      console.error('Fehler beim Speichern der E-Mail:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('E-Mail-Speicherung fehlgeschlagen:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Aktualisiert den Status einer E-Mail
 */
const updateEmailStatus = async (to, subject, status) => {
  try {
    const { supabase } = await import('../lib/supabase');
    
    const { error } = await supabase
      .from('email_queue_hb47x')
      .update({
        status: status,
        sent_at: status === 'sent' ? new Date().toISOString() : null
      })
      .eq('recipient', to)
      .eq('subject', subject)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(1);
      
    return !error;
  } catch (error) {
    console.error('Fehler beim Aktualisieren des E-Mail-Status:', error);
    return false;
  }
};

/**
 * Hilfsfunktionen
 */
function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function extractNameFromEmail(email) {
  return email.split('@')[0];
}

/**
 * Öffnet einen mailto-Link
 */
export const openMailtoLink = (to, subject, body) => {
  const cleanBody = stripHtml(body).substring(0, 1500);
  const mailtoLink = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(cleanBody)}`;
  window.open(mailtoLink);
};

/**
 * Test-Funktion für Mailgun API
 */
export const testMailgunConnection = async () => {
  try {
    console.log('🧪 Teste Mailgun API Verbindung...');
    
    const result = await sendEmail(
      'andreas.bolli@icloud.com',
      '🧪 Mailgun API Test - Wunsch-Burger',
      '<h2>Mailgun API Test erfolgreich!</h2><p>Die E-Mail-Integration funktioniert korrekt.</p>',
      'test'
    );
    
    return result;
  } catch (error) {
    console.error('Mailgun Test fehlgeschlagen:', error);
    return { success: false, error: error.message };
  }
};

// Legacy-Funktionen für Kompatibilität
export const sendEmailWithFallbacks = sendEmail;
export const sendEmailViaAPI = sendEmail;
export const sendEmailViaEmailJS = sendEmail;
export const sendRealSMTPEmail = sendEmail;
export const storeEmailForProcessing = storeEmailInQueue;
export const sendEmailWithFetch = sendEmail;